import React, { useState, useEffect } from 'react';
import { responseService } from '../../services/response.service';
import { httpService } from '../../services/http.service';
import { API_ENDPOINTS } from '../../config/api.config';

const DemandeDetailsModal = ({ demande, onClose }) => {
  const [responses, setResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [responsesError, setResponsesError] = useState(null);
  const [projectName, setProjectName] = useState(null);

  // Récupérer les réponses de la demande et le nom du projet
  useEffect(() => {
    const fetchData = async () => {
      if (demande && demande.id) {
        try {
          setLoadingResponses(true);
          setResponsesError(null);
          
          // Récupérer les réponses
          const responsesData = await responseService.getResponses(demande.id);
          setResponses(responsesData);
          
          // Récupérer le nom du projet si disponible
          if (demande.projet) {
            try {
              const projectData = await httpService.get(API_ENDPOINTS.PROJECTS.DETAIL(demande.projet));
              const name = projectData.name || projectData.title || projectData.nom || projectData.project_name || `Projet #${demande.projet}`;
              setProjectName(name);
            } catch (projectError) {
              console.warn('Erreur lors de la récupération du projet:', projectError);
              setProjectName(`Projet #${demande.projet}`);
            }
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des réponses:', error);
          // Si c'est un 404, l'endpoint n'existe pas encore
          if (error.message && error.message.includes('404')) {
            setResponses([]);
            setResponsesError(null);
          } else {
            setResponsesError('Erreur lors du chargement des réponses');
          }
        } finally {
          setLoadingResponses(false);
        }
      }
    };

    fetchData();
  }, [demande]);

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formater le statut
  const getStatusInfo = (demande) => {
    if (demande.has_technician) {
      return {
        text: 'Technicien assigné',
        class: 'success',
        icon: 'fas fa-check-circle'
      };
    } else if (demande.need_technician) {
      return {
        text: 'En attente d\'assignation',
        class: 'warning',
        icon: 'fas fa-clock'
      };
    } else {
      return {
        text: 'Non assigné',
        class: 'secondary',
        icon: 'fas fa-times-circle'
      };
    }
  };

  const statusInfo = getStatusInfo(demande);

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-file-alt me-2"></i>
              Détails de la demande #{demande.id}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Fermer"
            ></button>
          </div>
          
          <div className="modal-body">
            <div className="row">
              {/* Informations générales */}
              <div className="col-md-6">
                <h6 className="text-primary mb-3">
                  <i className="fas fa-info-circle me-2"></i>
                  Informations générales
                </h6>
                
                <div className="mb-3">
                  <label className="form-label fw-semibold">Statut</label>
                  <div>
                    <span className={`badge bg-${statusInfo.class}`}>
                      <i className={`${statusInfo.icon} me-1`}></i>
                      {statusInfo.text}
                    </span>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Date de soumission</label>
                  <p className="mb-0">{formatDate(demande.submitted_at)}</p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Demandeur</label>
                  <p className="mb-0">
                    <strong>{demande.name || 'N/A'}</strong>
                    {demande.email && (
                      <span className="text-muted d-block">
                        <i className="fas fa-envelope me-1"></i>
                        {demande.email}
                      </span>
                    )}
                    {demande.numero_tel && (
                      <span className="text-muted d-block">
                        <i className="fas fa-phone me-1"></i>
                        {demande.numero_tel}
                      </span>
                    )}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Projet associé</label>
                  <p className="mb-0">
                    {demande.projet ? (
                      <span className="badge bg-info">
                        {projectName || `Projet #${demande.projet}`}
                      </span>
                    ) : (
                      'N/A'
                    )}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Réponse</label>
                  <p className="mb-0">
                    {demande.reponse ? (
                      <span className="badge bg-success">Réponse #{demande.reponse}</span>
                    ) : (
                      'Aucune réponse'
                    )}
                  </p>
                </div>
              </div>

              {/* Informations du demandeur */}
              <div className="col-md-6">
                <h6 className="text-primary mb-3">
                  <i className="fas fa-user me-2"></i>
                  Informations du demandeur
                </h6>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Nom complet</label>
                  <p className="mb-0">{demande.name || 'N/A'}</p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Email</label>
                  <p className="mb-0">
                    {demande.email ? (
                      <a href={`mailto:${demande.email}`} className="text-decoration-none">
                        {demande.email}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Téléphone</label>
                  <p className="mb-0">
                    {demande.numero_tel ? (
                      <a href={`tel:${demande.numero_tel}`} className="text-decoration-none">
                        {demande.numero_tel}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </p>
                </div>
              </div>
            </div>

            <hr />

            {/* Informations du chantier */}
            <div className="row">
              <div className="col-12">
                <h6 className="text-primary mb-3">
                  <i className="fas fa-building me-2"></i>
                  Informations du chantier
                </h6>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Adresse du chantier</label>
                  <p className="mb-0">{demande.adresse || 'N/A'}</p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Code postal</label>
                  <p className="mb-0">{demande.post_code || 'N/A'}</p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Référence du chantier</label>
                  <p className="mb-0">{demande.reference_chantier || 'N/A'}</p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Référence du site de construction</label>
                  <p className="mb-0">{demande.construction_site_ref || 'N/A'}</p>
                </div>
              </div>

              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Étape du projet</label>
                  <p className="mb-0">{demande.project_step || 'N/A'}</p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Date de début du projet</label>
                  <p className="mb-0">
                    {demande.project_start_date ? (
                      new Date(demande.project_start_date).toLocaleDateString('fr-FR')
                    ) : (
                      'N/A'
                    )}
                  </p>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Besoin d'un technicien</label>
                  <p className="mb-0">
                    <span className={`badge bg-${demande.need_technician ? 'success' : 'secondary'}`}>
                      {demande.need_technician ? 'Oui' : 'Non'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Informations complémentaires */}
            {demande.complementary_info && (
              <>
                <hr />
                <div className="row">
                  <div className="col-12">
                    <h6 className="text-primary mb-3">
                      <i className="fas fa-comment me-2"></i>
                      Informations complémentaires
                    </h6>
                    <div className="bg-light p-3 rounded">
                      <p className="mb-0">{demande.complementary_info}</p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Éléments sélectionnés dans les sections */}
            <hr />
            <div className="row">
              <div className="col-12">
                <h6 className="text-primary mb-3">
                  <i className="fas fa-check-circle me-2"></i>
                  Éléments sélectionnés dans les sections
                </h6>
                <div className="alert alert-info mb-3" role="alert">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Note :</strong> Les sélections sont récupérées depuis l'API `/be/my-responses/` 
                  conformément au modèle UML. Les réponses sont liées aux contenus des sections.
                </div>
                
                {loadingResponses ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <p className="mt-2 mb-0">Chargement des réponses...</p>
                  </div>
                ) : responsesError ? (
                  <div className="alert alert-warning" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {responsesError}
                  </div>
                ) : responses.length > 0 ? (
                  <div className="responses-container">
                    <div className="alert alert-success mb-3" role="alert">
                      <i className="fas fa-check-circle me-2"></i>
                      <strong>{responses.length}</strong> élément{responses.length > 1 ? 's' : ''} sélectionné{responses.length > 1 ? 's' : ''} trouvé{responses.length > 1 ? 's' : ''}
                    </div>
                    {responses.map((response, index) => (
                      <div key={index} className="response-item mb-3 p-3 border rounded bg-light">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                            <span className={`badge me-2 ${
                              response.response_type === 'PRODUCT_SELECTION' ? 'bg-success' :
                              response.response_type === 'INPUT_VALUE' ? 'bg-info' :
                              response.response_type === 'OTHER_CHOICE_SELECTION' ? 'bg-warning' :
                              response.response_type === 'TEXT_RESPONSE' ? 'bg-primary' : 'bg-secondary'
                            }`}>
                              {response.response_type === 'PRODUCT_SELECTION' ? 'Produit' :
                               response.response_type === 'INPUT_VALUE' ? 'Saisie' :
                               response.response_type === 'OTHER_CHOICE_SELECTION' ? 'Choix' :
                               response.response_type === 'TEXT_RESPONSE' ? 'Texte' : 'Autre'}
                            </span>
                              <span className="text-muted small">
                                Contenu #{response.content}
                              </span>
                            </div>
                            <div className="response-value mb-2">
                              <strong>Valeur sélectionnée :</strong> 
                              <span className="ms-2 p-2 bg-white rounded border">{response.value}</span>
                            </div>
                            <div className="row">
                              {response.input_id && (
                                <div className="col-md-6">
                                  <small className="text-muted">
                                    <i className="fas fa-key me-1"></i>
                                    Input #{response.input_id}
                                  </small>
                                </div>
                              )}
                              <div className="col-md-6">
                                <small className="text-muted">
                                  <i className="fas fa-sort me-1"></i>
                                  Ordre : {response.order}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <i className="fas fa-info-circle text-muted mb-2" style={{fontSize: '2rem'}}></i>
                    <p className="text-muted mb-0">
                      {responsesError ? 
                        'Aucun élément sélectionné dans les sections' : 
                        'Aucun élément sélectionné dans les sections'
                      }
                    </p>
                    <small className="text-muted">
                      {!responsesError && 'Les sélections sont récupérées depuis l\'API `/be/my-responses/` selon le modèle UML.'}
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Documents associés */}
            {demande.documents && demande.documents.length > 0 && (
              <>
                <hr />
                <div className="row">
                  <div className="col-12">
                    <h6 className="text-primary mb-3">
                      <i className="fas fa-paperclip me-2"></i>
                      Documents associés
                    </h6>
                    <div className="list-group">
                      {demande.documents.map((docId, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <i className="fas fa-file me-2"></i>
                            Document #{docId}
                          </div>
                          <button className="btn btn-outline-primary btn-sm">
                            <i className="fas fa-download me-1"></i>
                            Télécharger
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemandeDetailsModal;